import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import UptimeStatusBadge from '../components/uptime/UptimeStatusBadge';
import UptimeMetricCard from '../components/uptime/UptimeMetricCard';
import UptimeCalendar from '../components/uptime/UptimeCalendar';
import ResponseTimeChart from '../components/uptime/ResponseTimeChart';
import { Button, Card, Tabs, Icon } from '../components/ui';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import {
  UptimeCheck,
  UptimeIncident,
  MonitoredSite,
  calculateAvailability,
  calculateApdex,
  calculateDowntime,
  calculateAvgResponseTime,
  findLongestDowntime,
  formatDuration,
  getApdexLabel,
} from '../lib/uptime';

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

type DateRange = '24h' | '7d' | '30d' | '90d';

const DATE_RANGES: DateRange[] = ['24h', '7d', '30d', '90d'];

const incidentHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 0',
  borderBottom: '1px solid var(--border-hairline)',
};

const incidentCellStyle: React.CSSProperties = {
  padding: '12px 0',
  fontSize: 'var(--text-sm)',
  whiteSpace: 'nowrap',
};

export default function UptimeDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const { user, isUserLoaded } = useUser();
  const [website, setMonitoredSite] = useState<MonitoredSite | null>(null);
  const [checks, setChecks] = useState<UptimeCheck[]>([]);
  const [incidents, setIncidents] = useState<UptimeIncident[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>('7d');

  // Check permissions
  const userRole = normalizeRole(user?.role);
  const hasAccess = userRole === 'admin' || userRole === 'developer' || userRole === 'agency_owner';

  const fetchData = useCallback(async () => {
    if (!siteId) return;

    try {
      // Fetch website
      const { data: websiteData, error: websiteError } = await supabase
        .from('monitored_sites')
        .select('id, name, url, is_active, alerts_snoozed_until')
        .eq('id', siteId)
        .single();

      if (websiteError) throw websiteError;
      setMonitoredSite(websiteData);

      // Calculate date range
      const startDate = new Date();
      switch (dateRange) {
        case '24h':
          startDate.setHours(startDate.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
      }

      // Fetch checks
      const { data: checksData, error: checksError } = await supabase
        .from('uptime_checks')
        .select('*')
        .eq('site_id', siteId)
        .gte('checked_at', startDate.toISOString())
        .order('checked_at', { ascending: false });

      if (checksError) throw checksError;
      setChecks(checksData || []);

      // Fetch incidents
      const { data: incidentsData, error: incidentsError } = await supabase
        .from('uptime_incidents')
        .select('*')
        .eq('site_id', siteId)
        .gte('started_at', startDate.toISOString())
        .order('started_at', { ascending: false });

      if (incidentsError) throw incidentsError;
      setIncidents(incidentsData || []);
    } catch (error) {
      console.error('Error fetching uptime detail:', error);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, dateRange]);

  useEffect(() => {
    // Wait for isUserLoaded: it flips true only after the Supabase session is
    // restored, so fetching earlier silently returns zero rows under RLS.
    if (isUserLoaded && hasAccess && siteId) {
      fetchData();
    }
  }, [isUserLoaded, hasAccess, siteId, fetchData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const availability = calculateAvailability(checks);
    const downtime = calculateDowntime(checks);
    const apdex = calculateApdex(checks);
    const avgResponseTime = calculateAvgResponseTime(checks);
    const longestDowntime = findLongestDowntime(incidents);
    const incidentCount = incidents.length;
    const latestCheck = checks.length > 0 ? checks[0] : null;

    return {
      availability,
      downtime,
      apdex,
      avgResponseTime,
      longestDowntime,
      incidentCount,
      latestCheck,
    };
  }, [checks, incidents]);

  if (!isUserLoaded) return null;

  if (!hasAccess) {
    return <Navigate to="/clients" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px] flex items-center justify-center min-h-screen">
          <div
            className="w-7 h-7 rounded-full animate-spin"
            style={{ border: '2px solid var(--accent)', borderTopColor: 'transparent' }}
          />
        </main>
      </div>
    );
  }

  if (!website) {
    return (
      <div className="min-h-screen">
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--text-muted)' }}>
              Site not found
            </p>
            <Link
              to="/uptime"
              className="hover-underline"
              style={{
                display: 'inline-block',
                marginTop: 'var(--space-3)',
                fontSize: 'var(--text-sm)',
                color: 'var(--accent-text)',
              }}
            >
              Back to Uptime
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        {/* Header */}
        <header
          className="flex flex-col md:flex-row md:items-center justify-between gap-3"
          style={{
            padding: 'var(--space-5) 0',
            borderBottom: '1px solid var(--border-hairline)',
            marginBottom: 'var(--space-6)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <Link
              to="/uptime"
              className="pressable hover-wash"
              title="Back to Uptime"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'var(--control-sm)',
                height: 'var(--control-sm)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-muted)',
                flexShrink: 0,
              }}
            >
              <Icon name="chevron-left" size={16} />
            </Link>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Wraps on phones rather than cutting the site name short */}
                <h1
                  className="md:truncate"
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-lg)',
                    fontWeight: 600,
                    letterSpacing: 'var(--tracking-tight)',
                    color: 'var(--text-body)',
                    minWidth: 0,
                  }}
                >
                  {website.name || 'Site Detail'}
                </h1>
                <UptimeStatusBadge isUp={metrics.latestCheck?.is_up ?? null} />
              </div>
              <span
                className="t-num"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
              >
                {website.url}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
            <a href={website.url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="secondary" icon="external-link">
                Visit Site
              </Button>
            </a>
            <span className="hover-spin" style={{ display: 'inline-flex' }}>
              <Button
                size="sm"
                variant="secondary"
                icon="refresh-cw"
                disabled={isRefreshing}
                onClick={handleRefresh}
              >
                {isRefreshing ? 'Checking...' : 'Refresh'}
              </Button>
            </span>
          </div>
        </header>

        {/* Date Range */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <Tabs
            tabs={[...DATE_RANGES]}
            active={dateRange}
            onChange={range => setDateRange(range as DateRange)}
          />
        </div>

        {/* Metrics Grid */}
        <div
          className="ck-stagger grid grid-cols-2 lg:grid-cols-4"
          style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}
        >
          <UptimeMetricCard
            title="Availability"
            value={`${metrics.availability.toFixed(2)}%`}
            color={metrics.availability >= 99.9 ? 'gray' : metrics.availability >= 99 ? 'yellow' : 'red'}
          />
          <UptimeMetricCard
            title="Downtime"
            value={`${metrics.downtime.minutes}m`}
            subtitle={`${metrics.downtime.percentage}% of time`}
            color={metrics.downtime.minutes === 0 ? 'gray' : 'red'}
          />
          <UptimeMetricCard
            title="Longest Downtime"
            value={formatDuration(metrics.longestDowntime)}
            color={metrics.longestDowntime === 0 ? 'gray' : 'red'}
          />
          <UptimeMetricCard
            title="Incidents"
            value={metrics.incidentCount}
            color={metrics.incidentCount === 0 ? 'gray' : 'red'}
          />
        </div>

        {/* Response Time Chart */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <ResponseTimeChart checks={checks} height={350} />
        </div>

        {/* Bottom Row: Apdex + Calendar */}
        <div
          className="grid grid-cols-1 lg:grid-cols-3"
          style={{ gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}
        >
          {/* Apdex & Avg Response */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <UptimeMetricCard
              title="Apdex Score"
              value={metrics.apdex.toFixed(2)}
              subtitle={getApdexLabel(metrics.apdex)}
              color={
                metrics.apdex >= 0.94
                  ? 'gray'
                  : metrics.apdex >= 0.85
                  ? 'blue'
                  : metrics.apdex >= 0.7
                  ? 'yellow'
                  : 'red'
              }
            />
            <UptimeMetricCard
              title="Avg Response Time"
              value={`${metrics.avgResponseTime}ms`}
              color={metrics.avgResponseTime < 500 ? 'gray' : metrics.avgResponseTime < 1000 ? 'yellow' : 'red'}
            />
          </div>

          {/* Calendar */}
          <div className="lg:col-span-2">
            <UptimeCalendar checks={checks} days={dateRange === '90d' ? 90 : dateRange === '30d' ? 30 : 7} />
          </div>
        </div>

        {/* Incidents */}
        <Card eyebrow="Incidents" title="Incident History">
          {incidents.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '32px 0',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-faint)',
              }}
            >
              No incidents recorded
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="t-eyebrow" style={incidentHeaderStyle}>Started</th>
                    <th className="t-eyebrow" style={incidentHeaderStyle}>Ended</th>
                    <th className="t-eyebrow" style={incidentHeaderStyle}>Duration</th>
                    <th className="t-eyebrow" style={incidentHeaderStyle}>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((incident, i) => (
                    <tr
                      key={incident.id}
                      className="hover-wash"
                      style={{
                        borderBottom:
                          i < incidents.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                      }}
                    >
                      <td className="t-num" style={{ ...incidentCellStyle, color: 'var(--text-body)' }}>
                        {new Date(incident.started_at).toLocaleString()}
                      </td>
                      <td className="t-num" style={incidentCellStyle}>
                        {incident.ended_at ? (
                          <span style={{ color: 'var(--text-body)' }}>
                            {new Date(incident.ended_at).toLocaleString()}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--status-down)', fontWeight: 500 }}>Ongoing</span>
                        )}
                      </td>
                      <td className="t-num" style={{ ...incidentCellStyle, color: 'var(--text-muted)' }}>
                        {incident.duration_seconds ? formatDuration(incident.duration_seconds) : '—'}
                      </td>
                      <td style={{ ...incidentCellStyle, color: 'var(--text-muted)', whiteSpace: 'normal' }}>
                        {incident.response_summary || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
